// src/containers/FormBuilderDashboard/components/PresetModal.jsx
import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import PresetSection from './PresetSection';
import { useSelector } from 'react-redux';
import _ from 'lodash';

const PresetModal = ({ open, editingPreset, presetForm, setPresetForm, onClose, onSave }) => {
  const all_users = useSelector(state => state.settings_users?.all_users || []);
  const default_profiles = useSelector(state => state.settings_users?.default_profiles || []).filter(
    profile => !['admin', 'super_admin'].includes(profile.name.toLowerCase())
  );
  const related_installations = useSelector(state => state.settings_dealerships?.related_installations || []);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    console.log(`[handleChange] ${field} changed to:`, value);
    setPresetForm(prev => ({ ...prev, [field]: value }));
  };

  const handleProfilesChange = (field) => (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [];
    const value = selected.includes('all') ? '' : [...new Set(selected)].join(',');
    setPresetForm(prev => ({ ...prev, [field]: value }));
  };

  const getProfileDisplayName = (id) => {
    const profile = _.find(default_profiles, { id: Number(id) });
    return profile?.name || `ID:${id}`;
  };

  const currentDealerships = presetForm.dealership
    ? presetForm.dealership.split(',').map(s => s.trim()).filter(Boolean)
    : [];

  const inputTypeOptions = [
    'text',
    'textarea',
    'checkbox',
    'select',
    'radio',
    'userstamp',
    'textstamp',
    'remove'
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{editingPreset ? 'Edit' : 'Add'} Work Item Preset</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#f9f9f9' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Name */}
          <PresetSection
            title="Name"
            helpText="The main name shown on the inspection form. Required."
            fields={[
              { name: 'name', label: 'Name', type: 'text' },
              { name: 'name_editable', label: 'Editable', type: 'switch' },
              { name: 'name_options', label: 'Options', type: 'text' },
              { name: 'name_inputtype', label: 'Input Type', type: 'select', options: inputTypeOptions },
              { name: 'name_locked', label: 'Locked', type: 'switch' },
              { name: 'name_allowed_profiles', label: 'Allowed Profiles', type: 'profiles', options: default_profiles },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
            handleProfilesChange={handleProfilesChange}
            getProfileDisplayName={getProfileDisplayName}
          />

          {/* Description */}
          <PresetSection
            title="Description"
            helpText="Optional details shown below the name."
            fields={[
              { name: 'description', label: 'Description', type: 'text', multiline: true, rows: 2 },
              { name: 'description_editable', label: 'Editable', type: 'switch' },
              { name: 'description_options', label: 'Options', type: 'text' },
              { name: 'description_inputtype', label: 'Input Type', type: 'select', options: inputTypeOptions },
              { name: 'description_locked', label: 'Locked', type: 'switch' },
              { name: 'description_allowed_profiles', label: 'Allowed Profiles', type: 'profiles', options: default_profiles },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
            handleProfilesChange={handleProfilesChange}
            getProfileDisplayName={getProfileDisplayName}
          />

          {/* Layout Type */}
          <PresetSection
            title="Layout Type"
            helpText="Select the layout type for this preset."
            fields={[
              { name: 'layout_type', label: 'Layout Type', type: 'select', options: ['default', '4_wheel_input'] },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
          />

          {/* Flags */}
          <PresetSection
            title="Flags"
            helpText="Control preset visibility and defaults."
            fields={[
              { name: 'active', label: 'Active', type: 'switch' },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
          />

          {/* Dealerships */}
          <PresetSection
            title="Dealerships"
            helpText="Select which dealerships this preset applies to. Leave blank for all."
            fields={[
              {
                name: 'dealership',
                label: 'Dealerships',
                type: 'dealerships',
                options: related_installations,
                value: currentDealerships,
                onChange: (e) => {
                  const selected = Array.isArray(e.target.value) ? e.target.value : [];
                  const newValue = selected.join(',');
                  setPresetForm(prev => ({ ...prev, dealership: newValue }));
                },
              },
            ]}
            presetForm={presetForm}
          />

          {/* Category */}
          <PresetSection
            title="Category"
            helpText="Used for filtering and reporting."
            fields={[
              { name: 'category', label: 'Category', type: 'select', options: ['cosmetic', 'mechanical', 'other'] },
              { name: 'category_editable', label: 'Editable', type: 'switch' },
              { name: 'category_options', label: 'Options', type: 'text' },
              { name: 'category_inputtype', label: 'Input Type', type: 'select', options: inputTypeOptions },
              { name: 'category_locked', label: 'Locked', type: 'switch' },
              { name: 'category_allowed_profiles', label: 'Allowed Profiles', type: 'profiles', options: default_profiles },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
            handleProfilesChange={handleProfilesChange}
            getProfileDisplayName={getProfileDisplayName}
          />

          {/* APPROVAL & STATUS - full support for required, recommend, recommended */}
          <PresetSection
            title="Approval & Status"
            helpText="Control pass, recommend, required, approved, declined, completed behavior."
            fields={[
              'pass',
              'recommend',
              'required',
              'recommended',
              'requested',
              'approved',
              'declined',
              'completed'
            ].map(field => ({
              name: field,
              label: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              type: 'group',
              subFields: [
                { name: field, label: 'Default On', type: 'switch' },
                { name: `${field}_editable`, label: 'Editable', type: 'switch' },
                { name: `${field}_locked`, label: 'Locked', type: 'switch' },
                { 
                  name: `${field}_inputtype`, 
                  label: 'Input Type', 
                  type: 'select', 
                  options: inputTypeOptions,
                  onChange: (e) => {
                    const newValue = e.target.value;
                    console.log(`[InputType Change] ${field}_inputtype changed to: ${newValue}`);
                    setPresetForm(prev => ({ ...prev, [`${field}_inputtype`]: newValue }));
                  }
                },
                { name: `${field}_options`, label: 'Options', type: 'text' },
                { name: `${field}_allowed_profiles`, label: 'Allowed Profiles', type: 'profiles', options: default_profiles },
              ],
            }))}
            presetForm={presetForm}
            handleChange={handleChange}
            handleProfilesChange={handleProfilesChange}
            getProfileDisplayName={getProfileDisplayName}
          />

          {/* RO Status */}
          <PresetSection
            title="RO Status"
            helpText="Default RO workflow status."
            fields={[
              { name: 'ro_status', label: 'Default Status', type: 'select', options: ['pending', 'approved', 'declined', 'completed'] },
              { name: 'ro_status_editable', label: 'Editable', type: 'switch' },
              { name: 'ro_status_options', label: 'Options', type: 'text' },
              { name: 'ro_status_inputtype', label: 'Input Type', type: 'select', options: inputTypeOptions },
              { name: 'ro_status_locked', label: 'Locked', type: 'switch' },
              { name: 'ro_status_allowed_profiles', label: 'Allowed Profiles', type: 'profiles', options: default_profiles },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
            handleProfilesChange={handleProfilesChange}
            getProfileDisplayName={getProfileDisplayName}
          />

          {/* Default Status */}
          <PresetSection
            title="Default Status"
            helpText="Initial status when added to vehicle."
            fields={[
              { name: 'default_status', label: 'Default', type: 'select', options: ['getapproved', 'approved', 'declined', 'completed'] },
              { name: 'default_status_editable', label: 'Editable', type: 'switch' },
              { name: 'default_status_options', label: 'Options', type: 'text' },
              { name: 'default_status_inputtype', label: 'Input Type', type: 'select', options: inputTypeOptions },
              { name: 'default_status_locked', label: 'Locked', type: 'switch' },
              { name: 'default_status_allowed_profiles', label: 'Allowed Profiles', type: 'profiles', options: default_profiles },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
            handleProfilesChange={handleProfilesChange}
            getProfileDisplayName={getProfileDisplayName}
          />

          {/* Pricing & Quantity */}
          <PresetSection
            title="Pricing & Quantity"
            helpText="Set default pricing and quantity."
            fields={[
              { name: 'parts_price', label: 'Parts Price', type: 'number' },
              { name: 'labor_price', label: 'Labor Price', type: 'number' },
              { name: 'labor_units', label: 'Labor Units', type: 'number' },
              { name: 'quantity', label: 'Quantity', type: 'number' },
              { name: 'parts_total', label: 'Parts Total', type: 'number' },
              { name: 'labor_total', label: 'Labor Total', type: 'number' },
              { name: 'estimate_total', label: 'Estimate Total', type: 'number' },
            ].map(field => ({
              name: field.name,
              label: field.label,
              type: 'group',
              subFields: [
                { name: field.name, label: field.label, type: field.type },
                { name: `${field.name}_editable`, label: 'Editable', type: 'switch' },
                { name: `${field.name}_options`, label: 'Options', type: 'text' },
                { name: `${field.name}_inputtype`, label: 'Input Type', type: 'select', options: inputTypeOptions },
                { name: `${field.name}_locked`, label: 'Locked', type: 'switch' },
                { name: `${field.name}_allowed_profiles`, label: 'Allowed Profiles', type: 'profiles', options: default_profiles },
              ],
            }))}
            presetForm={presetForm}
            handleChange={handleChange}
            handleProfilesChange={handleProfilesChange}
            getProfileDisplayName={getProfileDisplayName}
          />

          {/* Custom Headers */}
          <PresetSection
            title="Custom Headers"
            helpText="Add up to 6 custom fields."
            fields={[1, 2, 3, 4, 5, 6].map(num => ({
              name: `custom_header_${num}`,
              label: `Custom Header ${num}`,
              type: 'group',
              subFields: [
                { name: `custom_header_${num}`, label: 'Label', type: 'text' },
                { name: `custom_header_${num}_editable`, label: 'Editable', type: 'switch' },
                { name: `custom_header_${num}_options`, label: 'Options', type: 'text' },
                { name: `custom_header_${num}_inputtype`, label: 'Input Type', type: 'select', options: inputTypeOptions },
                { name: `custom_header_${num}_locked`, label: 'Locked', type: 'switch' },
                { name: `custom_header_${num}_allowed_profiles`, label: 'Allowed Profiles', type: 'profiles', options: default_profiles },
              ],
            }))}
            presetForm={presetForm}
            handleChange={handleChange}
            handleProfilesChange={handleProfilesChange}
            getProfileDisplayName={getProfileDisplayName}
          />

          {/* Automove */}
          <PresetSection
            title="Automove"
            helpText="Automatically move work item through statuses."
            fields={[
              { name: 'automove_counter', label: 'Automove Counter', type: 'number' },
              { name: 'automove_pending', label: 'Pending', type: 'switch' },
              { name: 'automove_getapproved', label: 'Get Approved', type: 'switch' },
              { name: 'automove_approved', label: 'Approved', type: 'switch' },
              { name: 'automove_waiting', label: 'Waiting', type: 'switch' },
              { name: 'automove_denied', label: 'Denied', type: 'switch' },
              { name: 'automove_trigger_value', label: 'Trigger Value', type: 'text' },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
          />

          {/* Assignment */}
          <PresetSection
            title="Assignment"
            helpText="Auto-assign to user."
            fields={[
              {
                name: 'auto_assign_user',
                label: 'Assign to User',
                type: 'select',
                options: all_users.map(u => ({
                  value: u.id,
                  label: u.user_name || u.email || `User ${u.id}`,
                })),
                includeNone: true,
              },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
          />

          {/* DVI PDF Settings */}
          <PresetSection
            title="DVI PDF Settings"
            helpText="Control visibility and content in PDF output."
            fields={[
              { name: 'dvi_pdf_show', label: 'PDF Show', type: 'switch' },
              { name: 'dvi_pdf_comments', label: 'DVI PDF Comments', type: 'text', multiline: true, rows: 2 },
              { name: 'dvi_description', label: 'DVI Description', type: 'text', multiline: true, rows: 2 },
            ]}
            presetForm={presetForm}
            handleChange={handleChange}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained">
          Save Preset
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PresetModal;